import React, { useEffect, useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  Banknote,
  Copy,
  FileJson,
  Info,
  Loader,
  Loader2,
  Phone,
} from "lucide-react";

import { isAlphanumeric, isNumeric } from "validator";
import { useParams } from "react-router-dom";

import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { fetchProfile } from "../../../Services/profile/profileservice";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import { verifyBankAccountAggregator } from "../../../Services/ApiTestService/apiTestService";

function BankAccountAggregator() {
  const [isLoading, setIsLoading] = useState(false);
  const [generateUrlResponse, setGenerateUrlresponse] = useState(null);
  const [fetchDetailResponse, setFetchDetailResponse] = useState(null);
  const [mobileNo, setMobileNo] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);
  const { serviceName } = useService();
  const [requestId, setRequestId] = useState("");

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isNumber = (value) => {
    if (!value) return false;

    return isNumeric(value, { no_symbols: true });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));

    setIsJsonCopied(true);
  };

  const handleAggregator = async () => {
    if (!profileData) return;

    const validateMobileNum = isNumber(mobileNo);

    if (!mobileNo) {
      return setInputError("Mobile Number is Required");
    }

    if (mobileNo && !validateMobileNum) {
      return setInputError("⚠️ Please enter Number in Mobile No. field");
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setGenerateUrlresponse(null);

      const res = await verifyBankAccountAggregator({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        mobile_num: mobileNo.trim()?.toUpperCase(),
        methodName: "generateUrl",
        return_url: "http://localhost:3000/bank-account-aggregator",
        // return_url: "https://apiuser.idspay.in/bank-account-aggregator",
      });

      if (res) {
        apiResponse = res;
      }
      setGenerateUrlresponse(apiResponse);

      if (res?.status?.code === 200) {
        localStorage.setItem("bankAggregatorRequestId", res?.data?.request_id);
        showSuccessToast(res?.message || "Success");

        window.location.href = res?.data?.url;
      } else {
        showErrorToast(
          res?.message || res?.data?.message || res?.data?.data?.message,
        );
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDetails = async (reqId) => {
    if (!profileData) return;

    try {
      setInputError(null);
      setIsLoading(true);
      setGenerateUrlresponse(null);

      const res = await verifyBankAccountAggregator({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        methodName: "fetchDetails",
        request_id: reqId,
      });

      if (res) {
        apiResponse = res;
      }
      setFetchDetailResponse(apiResponse);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
        localStorage.removeItem("bankAggregatorRequestId");
      } else {
        showErrorToast(
          res?.message || res?.data?.message || res?.data?.data?.message,
        );
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  useEffect(() => {
    const id = localStorage.getItem("bankAggregatorRequestId");

    if (!id) return;
    fetchDetails(id);
    setRequestId(id);
  }, []);

  useEffect(() => {
    if (!requestId) return;
    fetchDetails(requestId);
  }, [requestId]);

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center justify-start pb-14">
        {/* ---------------- FORM ---------------- */}
        <div className="max-w-4xl w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-100">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-slate-100 pb-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
              <Banknote size={24} />
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-800">
                {serviceName}
              </h2>
              <p className="text-sm text-slate-500">
                Verify Account details using Mobile number.
              </p>
            </div>
          </div>

          {/* Inputs */}
          <div className="mt-6  gap-6 flex justify-center items-center">
            {/* Mobile Number */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Phone size={17} className="text-blue-600" />
                Mobile Number <span className="text-red-600">*</span>
              </label>

              <input
                type="text"
                maxLength={10}
                value={mobileNo}
                onChange={(e) => setMobileNo(e.target.value)}
                placeholder="Eg: 7858987584"
                className="w-full rounded-xl border uppercase border-slate-300 px-4 py-3 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>

            {/* Button */}
            <div className="mt-8 flex ">
              <button
                disabled={isLoading || !mobileNo || mobileNo.length !== 10}
                onClick={handleAggregator}
                className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-7 py-3 font-semibold text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify
                    <ArrowRight
                      size={18}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Error */}
          {inputError && (
            <div className="mt-5 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm font-medium">{inputError}</span>
            </div>
          )}
        </div>

        {/* response message */}

        {generateUrlResponse && (
          <div
            className={` ${generateUrlResponse.status?.code === 500 || generateUrlResponse.status?.code === 422 ? "text-red-500" : "text-green-600"} py-4 text-2xl font-bold`}
          >
            {generateUrlResponse?.data?.message || generateUrlResponse?.message}
          </div>
        )}

        {/* instructions */}

        <div className="w-full flex justify-center">
          <div className="bg-green-100 max-w-3xl border border-green-400 text-green-700 p-5 mt-12 rounded-2xl">
            <h3 className="font-bold flex items-center justify-start gap-2 text-lg mb-2">
              <Info /> Important Instructions{" "}
              <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid Mobile Number. Clicking the Verify button.
              After successful verification, the system will return the
              generateUrlResponse in structured format, including details
              associated with the provided details. You may review and copy the
              data for your reference or further use.
            </p>
          </div>
        </div>

{/* ---------------- RESULT ---------------- */}
{fetchDetailResponse?.status?.code === 200 && (
  <div className="grid grid-cols-1 gap-6  mt-10 w-full max-w-[1400px] mx-auto">



    {/* =========================================================
        1. FULL STRUCTURED VIEW
    ========================================================= */}
    <div className="lg:col-span-7 flex flex-col gap-6 pr-2">

      {(() => {

        const dataObj = fetchDetailResponse?.data;
        const report = dataObj?.report;

        if (!report) {
          return (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <p className="text-sm font-semibold text-gray-600">
                Report data not available.
              </p>
            </div>
          );
        }


        /* =====================================================
           SAFE BANK / ACCOUNT DATA
        ===================================================== */

        const banks = Array.isArray(report?.banks)
          ? report.banks
          : [];

        const bankInfo = banks?.[0];

        const accounts = Array.isArray(bankInfo?.accounts)
          ? bankInfo.accounts
          : [];

        const accountInfo = accounts?.[0];

        const customer =
          accountInfo?.customer_info?.holders?.[0];

        const holdingType =
          accountInfo?.customer_info?.holding_type;


        /* =====================================================
           TRANSACTIONS
        ===================================================== */

        const transactions = Array.isArray(accountInfo?.transactions)
          ? accountInfo.transactions
          : [];


        /* =====================================================
           TRANSACTION SUMMARY
        ===================================================== */

        const totalCredits = transactions
          .filter((txn) => Number(txn?.amount || 0) > 0)
          .reduce(
            (sum, txn) => sum + Number(txn?.amount || 0),
            0
          );


        const totalDebits = transactions
          .filter((txn) => Number(txn?.amount || 0) < 0)
          .reduce(
            (sum, txn) =>
              sum + Math.abs(Number(txn?.amount || 0)),
            0
          );


        const creditCount = transactions.filter(
          (txn) => Number(txn?.amount || 0) > 0
        ).length;


        const debitCount = transactions.filter(
          (txn) => Number(txn?.amount || 0) < 0
        ).length;


        const netMovement =
          totalCredits - totalDebits;


        /* =====================================================
           HELPER
        ===================================================== */

        const InfoBlock = ({ label, value }) => (
          <div className="flex flex-col min-w-0">

            <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
              {label}
            </span>

            <span className="text-sm font-medium text-gray-900 break-words">
              {value === "" ||
              value === null ||
              value === undefined
                ? "N/A"
                : value}
            </span>

          </div>
        );


        /* =====================================================
           FORMAT CURRENCY
        ===================================================== */

        const formatCurrency = (value) => {
          return Number(value || 0).toLocaleString(
            "en-IN",
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          );
        };


        return (
          <>

            {/* =================================================
                1. REPORT METADATA
            ================================================= */}
            <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 border-t-4 border-t-blue-500">

              <h3 className="text-base font-bold text-gray-800 mb-4 border-b pb-2">
                Report Metadata
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">

                <InfoBlock
                  label="Request ID"
                  value={dataObj?.request_id}
                />

                <InfoBlock
                  label="Client Ref Num"
                  value={report?.client_ref_num}
                />

                <InfoBlock
                  label="TXN ID"
                  value={dataObj?.txn_id}
                />

                <InfoBlock
                  label="Report Type"
                  value={`${dataObj?.report_type || "N/A"} (${dataObj?.report_subtype || "N/A"})`}
                />

                <InfoBlock
                  label="Fetch Time"
                  value={
                    report?.report_fetch_time
                      ? report.report_fetch_time.split(".")[0]
                      : "N/A"
                  }
                />

                <InfoBlock
                  label="Data Source"
                  value={report?.source_of_data}
                />

                <InfoBlock
                  label="Statement Start"
                  value={report?.statement_start_date}
                />

                <InfoBlock
                  label="Statement End"
                  value={report?.statement_end_date}
                />

                <InfoBlock
                  label="Duration"
                  value={`${report?.duration_in_month || 0} Months`}
                />

                <InfoBlock
                  label="Fetch Type"
                  value={report?.report_fetch_type}
                />

                <InfoBlock
                  label="DG Version"
                  value={`v${report?.dg_report_version || "N/A"}`}
                />

                <InfoBlock
                  label="Report Num"
                  value={report?.report_num}
                />

                <InfoBlock
                  label="Multiple Accounts"
                  value={
                    report?.multiple_accounts_found
                      ? report.multiple_accounts_found.toUpperCase()
                      : "N/A"
                  }
                />

              </div>

            </div>


            {/* =================================================
                2. CUSTOMER INFORMATION
            ================================================= */}
            <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">

              <h3 className="text-base font-bold text-gray-800 mb-4 border-b pb-2">
                Customer Information
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">

                <InfoBlock
                  label="Full Name"
                  value={customer?.name}
                />

                <InfoBlock
                  label="PAN Number"
                  value={customer?.pan}
                />

                <InfoBlock
                  label="Date of Birth"
                  value={customer?.dob}
                />

                <InfoBlock
                  label="Email Address"
                  value={customer?.email}
                />

                <InfoBlock
                  label="Contact Number"
                  value={customer?.contact_number}
                />

                <InfoBlock
                  label="Landline"
                  value={customer?.landline}
                />

                <InfoBlock
                  label="CKYC Compliant"
                  value={customer?.ckyc_compliance}
                />

                <InfoBlock
                  label="Holding Type"
                  value={holdingType}
                />

                <InfoBlock
                  label="Nominee Status"
                  value={customer?.nominee}
                />

              </div>


              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">

                <InfoBlock
                  label="Registered Address"
                  value={customer?.address}
                />

              </div>

            </div>


            {/* =================================================
                3. ACCOUNT DETAILS
            ================================================= */}
            <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">

              <h3 className="text-base font-bold text-gray-800 mb-4 border-b pb-2">
                Account Details
              </h3>


              {/* Balance Highlight */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-blue-50 p-4 rounded-lg border border-blue-100 mb-5">

                <div>

                  <p className="text-xs uppercase tracking-wider text-blue-600 font-bold mb-1">
                    Current Balance
                  </p>

                  <p className="text-3xl font-black text-blue-700">
                    ₹
                    {formatCurrency(
                      accountInfo?.current_balance
                    )}
                  </p>

                  <p className="text-[10px] text-gray-500 mt-1">
                    As of{" "}
                    {accountInfo?.balance_date_time
                      ? accountInfo.balance_date_time.replace(
                          "T",
                          " "
                        )
                      : "N/A"}
                  </p>

                </div>


                <div className="md:text-right">

                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">
                    Pending Amount
                  </p>

                  <p className="text-sm font-bold text-gray-700">
                    ₹
                    {formatCurrency(
                      accountInfo?.pending?.amount
                    )}
                  </p>

                  <p className="text-[10px] text-gray-400 mt-1">
                    {accountInfo?.pending?.transaction_type ||
                      "No pending transaction"}
                  </p>

                </div>

              </div>


              {/* Account Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">

                <InfoBlock
                  label="Bank Name"
                  value={bankInfo?.bank}
                />

                <InfoBlock
                  label="Account Number"
                  value={accountInfo?.account_number}
                />

                <InfoBlock
                  label="Account Type"
                  value={accountInfo?.account_type}
                />

                <InfoBlock
                  label="Account Status"
                  value={accountInfo?.account_status}
                />

                <InfoBlock
                  label="IFSC Code"
                  value={accountInfo?.ifsc_code}
                />

                <InfoBlock
                  label="MICR Code"
                  value={accountInfo?.micr_code}
                />

                <InfoBlock
                  label="Facility"
                  value={accountInfo?.facility}
                />

                <InfoBlock
                  label="Opening Date"
                  value={accountInfo?.opening_date}
                />

                <InfoBlock
                  label="Location ID"
                  value={accountInfo?.location}
                />

                <InfoBlock
                  label="OD Limit"
                  value={`₹${formatCurrency(
                    accountInfo?.current_od_Limit
                  )}`}
                />

                <InfoBlock
                  label="Drawing Limit"
                  value={`₹${formatCurrency(
                    accountInfo?.drawing_limit
                  )}`}
                />

                <InfoBlock
                  label="Currency"
                  value={accountInfo?.currency}
                />

                <InfoBlock
                  label="Exchange Rate"
                  value={accountInfo?.exchge_rate}
                />

                <InfoBlock
                  label="Account Description"
                  value={accountInfo?.account_description}
                />

                <InfoBlock
                  label="Source Bank ID"
                  value={bankInfo?.source_bank_id}
                />

                <InfoBlock
                  label="Institution ID"
                  value={bankInfo?.digitap_institution_id}
                />

                <InfoBlock
                  label="Transaction Start"
                  value={accountInfo?.transaction_start_date}
                />

                <InfoBlock
                  label="Transaction End"
                  value={accountInfo?.transaction_end_date}
                />

              </div>

            </div>


            {/* =================================================
                4. TRANSACTION LEDGER
            ================================================= */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">

              {/* Transaction Header */}
              <div className="p-5 border-b border-gray-200">

                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">

                  <div>

                    <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">

                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>

                      Transactions Ledger

                    </h3>

                    <p className="text-xs text-gray-500 mt-1">

                      {accountInfo?.transaction_start_date ||
                        "N/A"}

                      {" → "}

                      {accountInfo?.transaction_end_date ||
                        "N/A"}

                    </p>

                  </div>


                  <div className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-xs font-bold border border-blue-100">

                    {transactions.length} Records

                  </div>

                </div>


                {/* =============================================
                    TRANSACTION SUMMARY
                ============================================= */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">

                  {/* Total Credit */}
                  <div className="bg-green-50 border border-green-100 rounded-lg p-3">

                    <p className="text-[10px] uppercase tracking-wider font-bold text-green-600">
                      Total Credit
                    </p>

                    <p className="text-lg font-bold text-green-700 mt-1">
                      +₹
                      {formatCurrency(totalCredits)}
                    </p>

                    <p className="text-[10px] text-green-600 mt-0.5">
                      {creditCount} transactions
                    </p>

                  </div>


                  {/* Total Debit */}
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3">

                    <p className="text-[10px] uppercase tracking-wider font-bold text-red-600">
                      Total Debit
                    </p>

                    <p className="text-lg font-bold text-red-600 mt-1">
                      -₹
                      {formatCurrency(totalDebits)}
                    </p>

                    <p className="text-[10px] text-red-500 mt-0.5">
                      {debitCount} transactions
                    </p>

                  </div>


                  {/* Net Movement */}
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">

                    <p className="text-[10px] uppercase tracking-wider font-bold text-blue-600">
                      Net Movement
                    </p>

                    <p
                      className={`text-lg font-bold mt-1 ${
                        netMovement >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {netMovement >= 0 ? "+" : "-"}₹
                      {formatCurrency(
                        Math.abs(netMovement)
                      )}
                    </p>

                    <p className="text-[10px] text-gray-500 mt-0.5">
                      Credit - Debit
                    </p>

                  </div>


                  {/* Current Balance */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">

                    <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">
                      Current Balance
                    </p>

                    <p className="text-lg font-bold text-gray-800 mt-1">
                      ₹
                      {formatCurrency(
                        accountInfo?.current_balance
                      )}
                    </p>

                    <p className="text-[10px] text-gray-500 mt-0.5">
                      Latest available balance
                    </p>

                  </div>

                </div>

              </div>


              {/* =============================================
                  TRANSACTION TABLE
              ============================================= */}

              {transactions.length === 0 ? (

                <div className="py-12 text-center">

                  <div className="text-gray-400 mb-2">
                    No Transactions
                  </div>

                  <p className="text-sm font-semibold text-gray-600">
                    No transaction records found
                  </p>

                  <p className="text-xs text-gray-400 mt-1">
                    The API did not return any transaction
                    records for this account.
                  </p>

                </div>

              ) : (

                <div className="overflow-x-auto">

                  <table className="w-full text-sm text-left">

                    {/* Table Header */}
                    <thead className="bg-gray-50 text-gray-600 text-[10px] uppercase tracking-wider border-b border-gray-200">

                      <tr>

                        <th className="px-4 py-3 font-bold whitespace-nowrap">
                          #
                        </th>

                        <th className="px-4 py-3 font-bold whitespace-nowrap">
                          Date & Time
                        </th>

                        <th className="px-4 py-3 font-bold min-w-[320px]">
                          Transaction Details
                        </th>

                        <th className="px-4 py-3 font-bold whitespace-nowrap">
                          Mode
                        </th>

                        <th className="px-4 py-3 font-bold text-right whitespace-nowrap">
                          Amount
                        </th>

                        <th className="px-4 py-3 font-bold text-right whitespace-nowrap">
                          Balance
                        </th>

                      </tr>

                    </thead>


                    {/* Table Body */}
                    <tbody className="divide-y divide-gray-100">

                      {transactions.map((txn, idx) => {

                        const amount = Number(
                          txn?.amount || 0
                        );

                        const isCredit = amount > 0;

                        const timestamp =
                          txn?.transaction_timestamp
                            ? txn.transaction_timestamp.split("T")
                            : [];


                        return (

                          <tr
                            key={
                              txn?.transaction_id ||
                              `${txn?.date}-${idx}`
                            }
                            className="hover:bg-gray-50 transition-colors align-top"
                          >

                            {/* =================================
                                #
                            ================================= */}
                            <td className="px-4 py-4 text-xs text-gray-400 font-semibold">
                              {idx + 1}
                            </td>


                            {/* =================================
                                DATE & TIME
                            ================================= */}
                            <td className="px-4 py-4 whitespace-nowrap">

                              <div className="font-semibold text-gray-800 text-xs">
                                {txn?.date || "N/A"}
                              </div>

                              <div className="text-[10px] text-gray-400 mt-1">
                                {timestamp?.[1] ||
                                  "Time N/A"}
                              </div>

                            </td>


                            {/* =================================
                                DETAILS
                            ================================= */}
                            <td className="px-4 py-4">

                              {/* Narration */}
                              <div className="text-gray-800 text-[13px] font-semibold leading-relaxed break-words">

                                {txn?.narration ||
                                  "No narration available"}

                              </div>


                              {/* Transaction Metadata */}
                              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">

                                <div className="text-[10px] text-gray-500 break-all">

                                  <span className="font-bold text-gray-400">
                                    Reference:
                                  </span>{" "}

                                  {txn?.reference ||
                                    "N/A"}

                                </div>


                                <div className="text-[10px] text-gray-500 break-all">

                                  <span className="font-bold text-gray-400">
                                    Transaction ID:
                                  </span>{" "}

                                  {txn?.transaction_id ||
                                    "N/A"}

                                </div>


                                <div className="text-[10px] text-gray-500 break-all">

                                  <span className="font-bold text-gray-400">
                                    Cheque:
                                  </span>{" "}

                                  {txn?.cheque_num ||
                                    "N/A"}

                                </div>


                                <div className="text-[10px] text-gray-500 break-all">

                                  <span className="font-bold text-gray-400">
                                    Timestamp:
                                  </span>{" "}

                                  {txn?.transaction_timestamp ||
                                    "N/A"}

                                </div>

                              </div>

                            </td>


                            {/* =================================
                                MODE
                            ================================= */}
                            <td className="px-4 py-4 whitespace-nowrap">

                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide border ${
                                  txn?.mode_from_source ===
                                  "UPI"
                                    ? "bg-purple-50 text-purple-700 border-purple-100"
                                    : txn?.mode_from_source ===
                                      "ATM"
                                    ? "bg-orange-50 text-orange-700 border-orange-100"
                                    : "bg-gray-100 text-gray-600 border-gray-200"
                                }`}
                              >
                                {txn?.mode_from_source ||
                                  "N/A"}
                              </span>

                            </td>


                            {/* =================================
                                AMOUNT
                            ================================= */}
                            <td className="px-4 py-4 text-right whitespace-nowrap">

                              <div
                                className={`font-bold ${
                                  isCredit
                                    ? "text-green-600"
                                    : "text-red-500"
                                }`}
                              >

                                {isCredit ? "+" : "-"}₹
                                {formatCurrency(
                                  Math.abs(amount)
                                )}

                              </div>


                              <div
                                className={`text-[9px] uppercase font-semibold mt-1 ${
                                  isCredit
                                    ? "text-green-500"
                                    : "text-red-400"
                                }`}
                              >
                                {isCredit
                                  ? "Credit"
                                  : "Debit"}
                              </div>

                            </td>


                            {/* =================================
                                RUNNING BALANCE
                            ================================= */}
                            <td className="px-4 py-4 text-right whitespace-nowrap">

                              <div className="font-semibold text-gray-700">

                                ₹
                                {formatCurrency(
                                  txn?.balance
                                )}

                              </div>

                              <div className="text-[9px] text-gray-400 mt-1">
                                Running Balance
                              </div>

                            </td>

                          </tr>

                        );

                      })}

                    </tbody>

                  </table>

                </div>

              )}

            </div>


            {/* =================================================
                5. TRANSACTION PERIOD / ACCOUNT STATEMENT INFO
            ================================================= */}
            <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">

              <h3 className="text-base font-bold text-gray-800 mb-4 border-b pb-2">
                Statement Information
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">

                <InfoBlock
                  label="Statement Start"
                  value={report?.statement_start_date}
                />

                <InfoBlock
                  label="Statement End"
                  value={report?.statement_end_date}
                />

                <InfoBlock
                  label="Transaction Start"
                  value={accountInfo?.transaction_start_date}
                />

                <InfoBlock
                  label="Transaction End"
                  value={accountInfo?.transaction_end_date}
                />

                <InfoBlock
                  label="Total Transactions"
                  value={transactions.length}
                />

                <InfoBlock
                  label="Credit Transactions"
                  value={creditCount}
                />

                <InfoBlock
                  label="Debit Transactions"
                  value={debitCount}
                />

                <InfoBlock
                  label="Tamper Detection"
                  value={
                    Array.isArray(
                      accountInfo?.tamper_detection_details
                    )
                      ? accountInfo.tamper_detection_details
                          .length === 0
                        ? "No Issues"
                        : `${accountInfo.tamper_detection_details.length} Records`
                      : "N/A"
                  }
                />

              </div>

            </div>

          </>
        );

      })()}

    </div>

        {/* =========================================================
        1. RAW JSON VIEW
    ========================================================= */}
    <div className="lg:col-span-5 bg-gray-900 relative text-green-300 p-4 rounded-xl shadow-lg overflow-auto max-h-[900px] custom-scrollbar">

      <button
        onClick={() => handleCopy(fetchDetailResponse)}
        className="absolute text-white top-4 right-6 hover:text-gray-300 transition-colors"
      >
        {isJsonCopied ? (
          <span className="text-sm">Copied!</span>
        ) : (
          <Copy size={20} />
        )}
      </button>

      <p className="font-bold mb-4 text-xl flex items-center gap-2 text-white border-b border-gray-700 pb-2">
        <FileJson size={24} />
        JSON Response
      </p>

      <pre className="text-xs whitespace-pre-wrap break-words">
        {JSON.stringify(fetchDetailResponse, null, 2)}
      </pre>

    </div>


  </div>
)}
      </div>
    </>
  );
}

export default BankAccountAggregator;
